import React from 'react';
import { createSlice } from '@reduxjs/toolkit';
import { apiCallBegan, ApiErrorPayload } from 'store/middleware/api/api';
import { toast } from '@oliasoft-open-source/react-ui-library';
import i18n from 'i18next';
import translations from 'src/internationalisation/translation-map.json';
import type {
  campaignTimeTrackerItem,
  ICampaignActiveEstimate,
  ICampaignEstimate,
  ICampaignOperationSequenceItem,
  ICampaignSimulation,
  ICampaignState,
  ICampaignTotalItem,
  ILearningCurveData,
  IRunCampaignData,
} from 'src/common/interfaces/campaigns.interfaces';
import { ISimulationResult } from 'src/common/interfaces/simulations.interfaces';
import { CampaignStatus } from 'src/enums/campaigns';

const initialState: ICampaignState = {
  isAdding: false,
  isFetching: false,
  isFetchingCampaignResults: false,
  isRecalculating: false,
  isGeneratingEstimates: false,
  campaignModalVisible: false,
  welldesignModalVisible: false,
  learningCurveModalVisible: false,
  kpiModalVisible: false,
  list: [],
  estimates: [],
  designs: [],
  estimatesList: [],
  countries: [],
  campaignTotal: [],
  timeTracker: [],
  timeTrackerCampaignId: null,
  isFetchingCampaignTimeTracker: false,
  isGeneratingTimeTracker: false,
  timeTrackerGenerationConflict: null,
  operationSequenceDirtyCampaignIds: [],
  projects: [],
};

interface ICampaignActualConflict {
  campaignId: string;
  campaignName: string;
  estimateName: string;
}

interface ICampaignStatusError extends ApiErrorPayload {
  conflicts?: ICampaignActualConflict[];
}

const getCampaignStatusErrorContent = (error: ICampaignStatusError) => {
  if (!error.conflicts?.length) {
    return Array.isArray(error.errorMessages) ? error.errorMessages.join('. ') : error.errorMessages || error.message;
  }

  const conflictsByCampaign = error.conflicts.reduce((result, conflict) => {
    const existingConflict = result.get(conflict.campaignId);
    if (existingConflict) {
      existingConflict.estimateNames.push(conflict.estimateName);
      return result;
    }

    result.set(conflict.campaignId, {
      campaignName: conflict.campaignName,
      estimateNames: [conflict.estimateName],
    });

    return result;
  }, new Map<string, { campaignName: string; estimateNames: string[] }>());

  const message = Array.isArray(error.errorMessages)
    ? error.errorMessages.join('. ')
    : error.errorMessages || error.message;

  return React.createElement(
    'div',
    null,
    message && React.createElement('div', { style: { marginBottom: '8px' } }, message),
    ...Array.from(conflictsByCampaign.values()).map(({ campaignName, estimateNames }) =>
      React.createElement(
        'div',
        { key: campaignName, style: { marginTop: '8px' } },
        React.createElement('strong', null, `"${campaignName}"`),
        React.createElement(
          'ul',
          { style: { margin: '4px 0 0 18px', padding: 0 } },
          ...estimateNames.map((estimateName) => React.createElement('li', { key: estimateName }, estimateName)),
        ),
      ),
    ),
  );
};

const campaigns = createSlice({
  name: 'campaigns',
  initialState,
  reducers: {
    listRequested: (campaigns) => {
      campaigns.isFetching = true;
    },
    listFetched: (campaigns, action) => {
      campaigns.isFetching = false;
      campaigns.list = action.payload.campaigns.map((campaign: any) => ({
        ...campaign,
        estimates: campaign.estimates ?? [],
        designs: campaign.designs ?? [],
        kpisList: campaign.kpisList ?? [],
        sections: campaign.sections ?? [],
        detailsFetched: !!campaign.estimates,
      }));
      campaigns.estimatesList = action.payload.estimatesList ?? [];
      campaigns.estimates = (action.payload.estimates ?? []).map((e: ICampaignActiveEstimate) => ({
        ...e,
        visible: true,
      }));
      campaigns.countries = action.payload.countries ?? [];
      campaigns.campaignTotal = action.payload.campaignTotal ?? [];
    },
    listRequestFailed: (campaigns) => {
      campaigns.isFetching = false;
    },
    campaignAddRequested: (campaigns) => {
      campaigns.isAdding = true;
    },
    campaignAdded: (campaigns, action) => {
      campaigns.isAdding = false;
      const existingIndex = campaigns.list.findIndex((item) => item.campaignId === action.payload.campaignId);

      if (existingIndex === -1) {
        const index =
          campaigns.list.push({
            ...action.payload,
            estimates: [],
            kpisList: [],
            isSynced: true,
          }) - 1;
        campaigns.list = campaigns.list.map((item, i) => {
          item.active = i === index;
          return item;
        });
      } else {
        campaigns.list[existingIndex] = {
          ...campaigns.list[existingIndex],
          ...action.payload,
        };
        campaigns.list[existingIndex].active = true;
      }
      if (action.payload.status === CampaignStatus.Prototype) {
        if (campaigns.timeTrackerCampaignId === action.payload.campaignId) {
          campaigns.timeTracker = [];
          campaigns.timeTrackerCampaignId = null;
        }
        campaigns.isFetchingCampaignTimeTracker = false;
        campaigns.timeTrackerGenerationConflict = null;
        campaigns.operationSequenceDirtyCampaignIds = campaigns.operationSequenceDirtyCampaignIds.filter(
          (campaignId) => campaignId !== action.payload.campaignId,
        );
      }
    },
    campaignAddRequestedFailed: (campaigns) => {
      campaigns.isAdding = false;
    },
    campaignSelected: (campaigns, action) => {
      const { list } = campaigns;
      const index = list.findIndex((campaign) => campaign.campaignId === action.payload);
      campaigns.list = list.map((item, i) => ({
        ...item,
        active: i === index,
      }));
    },
    campaignDetailsRequested: (campaigns, action) => {
      const index = campaigns.list.findIndex((campaign) => campaign.campaignId === action.payload.campaignId);
      if (index !== -1) {
        campaigns.list[index].isFetchingDetails = true;
      }
    },
    campaignDetailsFetched: (campaigns, action) => {
      const { campaign } = action.payload;
      const index = campaigns.list.findIndex((item) => item.campaignId === campaign.campaignId);
      if (index !== -1) {
        campaigns.list[index] = {
          ...campaigns.list[index],
          ...campaign,
          estimates: campaign.estimates ?? [],
          designs: campaign.designs ?? [],
          kpisList: campaign.kpisList ?? [],
          sections: campaign.sections ?? [],
          estimatesCount: campaign.estimates?.length ?? 0,
          detailsFetched: true,
          isFetchingDetails: false,
        };
      }
    },
    campaignDetailsRequestFailed: (campaigns, action) => {
      const index = campaigns.list.findIndex((campaign) => campaign.campaignId === action.payload.campaignId);
      if (index !== -1) {
        campaigns.list[index].isFetchingDetails = false;
      }
    },
    campaignRemoved: (campaigns, action) => {
      campaigns.list = campaigns.list.filter((campaign) => campaign.campaignId !== action.payload);
    },
    campaignModalOpened: (campaigns, action) => {
      const { list } = campaigns;
      const index = list.findIndex((campaign) => campaign.campaignId === action.payload);
      campaigns.list = list.map((item, i) => {
        item.active = i === index;
        return item;
      });

      campaigns.campaignModalVisible = true;
    },
    campaignModalClosed: (campaigns) => {
      campaigns.campaignModalVisible = false;
    },
    estimateRemoved: (campaigns, action) => {
      const { list } = campaigns;
      const campaignIndex = list.findIndex((campaign) => campaign.campaignId === action.payload.campaignId);
      if (campaignIndex !== -1) {
        const { estimates } = list[campaignIndex];
        const estimateIndex = estimates.findIndex((estimate) => estimate.campaignsEstimatesId === action.payload.id);
        if (estimateIndex !== -1) {
          delete estimates[estimateIndex];
          campaigns.list[campaignIndex].estimates = estimates.filter((i) => i);
        }
      }
    },
    learningCurveModalUpdated: (campaigns, action) => {
      campaigns.learningCurveModalVisible = action.payload;
    },
    learningCurveUpdateRequested: (campaigns, action) => {
      campaigns.isAdding = true;
      const { campaignId, estimates } = action.payload;
      const { list } = campaigns;
      const campaignIndex = list.findIndex((campaign) => campaign.campaignId === campaignId);
      if (campaignIndex !== -1) {
        for (const estimate of estimates) {
          const estimateIndex = list[campaignIndex].estimates.findIndex(
            (item) => item.campaignsEstimatesId === estimate.campaignsEstimatesId,
          );
          if (estimateIndex !== -1) {
            campaigns.list[campaignIndex].estimates[estimateIndex].adjustment = estimate.adjustment;
          }
        }
      }
    },
    learningCurveUpdated: (campaigns) => {
      campaigns.isAdding = false;
      campaigns.learningCurveModalVisible = false;
    },
    learningCurveUpdateRequestFailed: (campaigns) => {
      campaigns.isAdding = false;
    },
    learningCurveFlagUpdateRequested: (campaigns, action) => {
      const { campaignId } = action.payload;
      const { list } = campaigns;
      const campaignIndex = list.findIndex((campaign) => campaign.campaignId === campaignId);
      if (campaignIndex !== -1) {
        campaigns.list[campaignIndex].learningCurve = !campaigns.list[campaignIndex].learningCurve;
      }
    },
    reorderEstimatesRequested: (campaigns, action) => {
      campaigns.isAdding = true;
      const { campaignId, from, to } = action.payload;
      const campaignIndex = campaigns.list.findIndex((campaign) => campaign.campaignId === campaignId);
      if (campaignIndex !== -1) {
        const { estimates } = campaigns.list[campaignIndex];
        const updated = [...estimates];
        const [movedItem] = updated.splice(from, 1);
        updated.splice(to, 0, movedItem);

        campaigns.list[campaignIndex].estimates = updated.map((item, index) => ({
          ...item,
          position: index,
        }));
      }
    },
    reorderEstimatesFetched: (campaigns) => {
      campaigns.isAdding = false;
    },
    kpiModalUpdated: (campaigns, action) => {
      campaigns.kpiModalVisible = action.payload;
    },
    kpisAddRequested: (campaigns) => {
      campaigns.isAdding = true;
    },
    kpisAdded: (campaigns, action) => {
      const existingIndex = campaigns.list.findIndex((item) => item.campaignId === action.payload.campaignId);
      if (existingIndex !== -1) {
        campaigns.list[existingIndex].kpisList = action.payload.kpisList;
      }
      campaigns.isAdding = false;
      campaigns.kpiModalVisible = false;
    },
    kpisAddRequestFailed: (campaigns) => {
      campaigns.isAdding = false;
    },
    kpiRemoved: (campaigns, action) => {
      const { list } = campaigns;
      const campaignIndex = list.findIndex((campaign) => campaign.campaignId === action.payload.campaignId);
      if (campaignIndex !== -1) {
        const { kpisList } = list[campaignIndex];
        const kpiIndex = kpisList.findIndex((kpi) => kpi.id === action.payload.id);
        if (kpiIndex !== -1) {
          delete kpisList[kpiIndex];
          campaigns.list[campaignIndex].kpisList = kpisList.filter((i) => i);
        }
      }
    },
    welldesignModalUpdated: (campaigns, action) => {
      campaigns.welldesignModalVisible = action.payload;
    },
    campaignConceptDesignsAdded: (campaigns, action) => {
      campaigns.isAdding = false;
      const { list } = campaigns;
      const index = list.findIndex((item) => item.campaignId === action.payload.campaignId);
      if (index !== -1) {
        action.payload.active = true;
        campaigns.list[index] = action.payload;
      }
    },
    conceptDesignRemoved: (campaigns, action) => {
      const { campaignId, datasetId, projectId } = action.payload;
      const { list } = campaigns;
      const index = list.findIndex((item) => item.campaignId === campaignId);
      const designIndex = list[index].designs.findIndex((design) =>
        datasetId === null ? design.projectId === projectId : design.datasetId === datasetId,
      );
      list[index].designs.splice(designIndex, 1);
    },
    campaignRunConceptFetched: (campaigns, action) => {
      campaigns.estimates = action.payload.estimates;
      campaigns.list = campaigns.list.map((campaign) => {
        if (campaign.campaignId === action.payload.campaign.campaignId) {
          return {
            ...action.payload.campaign,
            active: true,
          };
        } else {
          return {
            ...campaign,
            active: false,
          };
        }
      });
      campaigns.campaignTotal = action.payload.campaignTotal;
      campaigns.isGeneratingEstimates = false;
      campaigns.isRecalculating = false;
    },
    simulationRequested: (campaigns, action) => {
      const { estimates } = campaigns;
      const index = estimates.findIndex((estimate) => estimate.projectId === action.payload.projectId);
      if (index !== -1) {
        campaigns.estimates[index].isFetching = true;
      }
    },
    simulationFetched: (campaigns, action) => {
      const { projectId, ...simulation } = action.payload;
      const { estimates } = campaigns;
      campaigns.list.forEach((campaign) => {
        campaign.estimates.forEach((estimate) => {
          if (estimate.projectId === projectId) {
            estimate.simulation = simulation;
          }
        });
      });
      const index = estimates.findIndex((estimate) => estimate.projectId === projectId);
      if (index !== -1) {
        campaigns.estimates[index].simulationResult = action.payload;
        campaigns.estimates[index].isFetching = false;
        campaigns.estimates[index].simulation.fetched = true;
      }
    },
    simulationRequestFailed: (campaigns, action) => {
      const { estimates } = campaigns;
      const index = estimates.findIndex((estimate) => estimate.projectId === action.payload.projectId);
      if (index !== -1) {
        campaigns.estimates[index].isFetching = false;
      }
    },
    addSimulationStatus: (campaigns, action) => {
      const { estimates, campaignTotal } = campaigns;
      const index = estimates.findIndex((estimate) => {
        return estimate.jobId === action.payload.metadata.jobId;
      });
      const campaignTotalIndex = campaignTotal?.findIndex((item) => {
        return item.jobId === action.payload.metadata.jobId;
      });
      if (index !== -1) {
        campaigns.estimates[index].simulation = {
          status: action.payload.status,
          message: action.payload.message,
          progress: action.payload.progress,
        } as ICampaignSimulation;
      }
      if (campaignTotalIndex !== -1) {
        campaigns.campaignTotal[campaignTotalIndex].simulation = {
          status: action.payload.status,
          message: action.payload.message,
          progress: action.payload.progress,
          fetched: action.payload.fetced,
        };
      }
    },

    campaignSimulationFetched: (campaigns, action) => {
      const { campaignId, ...simulation } = action.payload;
      const { campaignTotal } = campaigns;
      campaigns.list.forEach((campaign) => {
        if (campaign.campaignTotal?.campaignId === campaignId) {
          campaign.campaignTotal.simulation = simulation;
        }
      });
      const index = campaignTotal?.findIndex((campaign) => campaign.campaignId === campaignId);

      if (index !== -1) {
        campaigns.campaignTotal[index].simulationResult = action.payload;
        campaigns.campaignTotal[index].isFetching = false;
        campaigns.campaignTotal[index].simulation.fetched = true;
      }
    },
    campaignSimulationRequested: (campaigns, action) => {
      const { campaignTotal } = campaigns;
      const index = campaignTotal?.findIndex((item) => item.campaignId === action.payload.campaignId);
      if (index !== -1) {
        campaigns.campaignTotal[index].isFetching = true;
      }
    },
    campaignSimulationRequestFailed: (campaigns, action) => {
      const { campaignTotal } = campaigns;
      const index = campaignTotal?.findIndex((item) => item.campaignId === action.payload.campaignId);
      if (index !== -1) {
        campaigns.campaignTotal[index].isFetching = false;
      }
    },
    designsFetched: (campaigns, action) => {
      campaigns.designs = action.payload.designs;
      campaigns.projects = action.payload.projects;
      campaigns.isFetching = false;
    },
    designsFetchFailed: (campaigns) => {
      campaigns.isFetching = false;
    },
    campaignResultsRequested: (campaigns) => {
      campaigns.isFetchingCampaignResults = true;
    },
    campaignResultsFetched: (campaigns, action) => {
      const {
        campaignId,
        estimates: payloadEstimates,
        kpisList,
        campaignTotal,
        isSynced,
        operationSequence,
      } = action.payload;
      const updatedList = campaigns.list?.map((item) =>
        item.campaignId === campaignId
          ? {
              ...item,
              estimates:
                item.estimates?.length > 0
                  ? item.estimates.map((estimate) => {
                      const matchingEstimate = payloadEstimates.find(
                        (pe: ICampaignEstimate) => pe.campaignsEstimatesId === estimate.campaignsEstimatesId,
                      );

                      return {
                        ...estimate,
                        simulation: matchingEstimate?.simulation ?? estimate.simulation,
                        sections: matchingEstimate?.sections ?? [],
                      };
                    })
                  : payloadEstimates,
              kpisList,
              campaignTotal,
              isSynced,
              operationSequence,
              detailsFetched: true,
            }
          : item,
      );
      campaigns.list = updatedList;
      campaigns.isFetchingCampaignResults = false;
    },
    runCampaignTotalRequested: (campaigns, action) => {
      const { campaignId } = action.payload;
      campaigns.campaignTotal = campaigns.campaignTotal.map((campaign) => {
        if (campaign.campaignId === campaignId) {
          return {
            ...campaign,
            simulation: {} as ICampaignSimulation,
            simulationResult: {} as ISimulationResult,
          };
        }
        return campaign;
      });
      campaigns.isRecalculating = true;
    },
    runCampaignTotalFetched: (campaigns, action) => {
      const { campaignId } = action.payload;
      const updatedCampaignTotals = action.payload.campaignTotal;

      campaigns.campaignTotal = campaigns.campaignTotal.map((campaign) => {
        const updatedCampaign = updatedCampaignTotals.find(
          (updated: ICampaignTotalItem) => updated.campaignId === campaign.campaignId,
        );
        return updatedCampaign ? { ...campaign, ...updatedCampaign } : campaign;
      });
      campaigns.list = campaigns.list.map((campaign) => {
        return campaign.campaignId === campaignId ? { ...campaign, isSynced: true } : campaign;
      });
    },
    campaignsCleanup: (campaigns) => {
      campaigns.list = [];
      campaigns.estimates = [];
      campaigns.designs = [];
      campaigns.estimatesList = [];
      campaigns.countries = [];
      campaigns.campaignTotal = [];
      campaigns.timeTracker = [];
      campaigns.timeTrackerCampaignId = null;
      campaigns.isFetchingCampaignTimeTracker = false;
      campaigns.isGeneratingTimeTracker = false;
      campaigns.timeTrackerGenerationConflict = null;
      campaigns.operationSequenceDirtyCampaignIds = [];
    },
    updateIsRecalculating: (campaigns, action) => {
      campaigns.isRecalculating = action.payload;
    },
    campaignRunConceptRequested: (campaigns) => {
      campaigns.isRecalculating = true;
      campaigns.isGeneratingEstimates = true;
    },
    campaignRunConcepRequestedFailed: (campaigns) => {
      campaigns.isRecalculating = false;
      campaigns.isGeneratingEstimates = false;
    },
    timeTrackerRequested: (campaigns, action) => {
      campaigns.isFetchingCampaignTimeTracker = true;
      campaigns.timeTrackerCampaignId = action.payload;
      campaigns.timeTracker = [];
    },
    timeTrackerFetched: (campaigns, action) => {
      campaigns.isFetchingCampaignTimeTracker = false;
      if (Array.isArray(action.payload)) {
        campaigns.timeTracker = action.payload;
        campaigns.timeTrackerCampaignId = action.payload[0]?.campaignId ?? null;
        return;
      }
      const campaign = campaigns.list.find((item) => item.campaignId === action.payload.campaignId);
      if (campaign?.status !== CampaignStatus.Actual) {
        campaigns.timeTracker = [];
        campaigns.timeTrackerCampaignId = null;
        return;
      }
      campaigns.timeTracker = action.payload.timeTracker;
      campaigns.timeTrackerCampaignId = action.payload.campaignId;
    },
    timeTrackerRequestFailed: (campaigns) => {
      campaigns.isFetchingCampaignTimeTracker = false;
      campaigns.timeTrackerCampaignId = null;
      campaigns.timeTracker = [];
    },
    operationSequenceReorderRequested: (campaigns, action) => {
      const { campaignId, sequence } = action.payload;
      const campaignIndex = campaigns.list.findIndex((campaign) => campaign.campaignId === campaignId);
      if (campaignIndex !== -1) {
        campaigns.list[campaignIndex].operationSequence = sequence;
      }
      if (!campaigns.operationSequenceDirtyCampaignIds.includes(campaignId)) {
        campaigns.operationSequenceDirtyCampaignIds.push(campaignId);
      }
    },
    operationSequenceReorderFetched: (campaigns, action) => {
      const { campaignId, operationSequence } = action.payload;
      const campaignIndex = campaigns.list.findIndex((campaign) => campaign.campaignId === campaignId);
      if (campaignIndex !== -1) {
        campaigns.list[campaignIndex].operationSequence = operationSequence;
      }
    },
    campaignTimeTrackerGenerateRequested: (campaigns) => {
      campaigns.isGeneratingTimeTracker = true;
    },
    campaignTimeTrackerGenerated: (campaigns, action) => {
      const { campaignId, start, timeTracker, operationSequence } = action.payload;
      campaigns.isGeneratingTimeTracker = false;
      campaigns.timeTrackerGenerationConflict = null;
      campaigns.timeTrackerCampaignId = campaignId;
      campaigns.isFetchingCampaignTimeTracker = false;
      campaigns.operationSequenceDirtyCampaignIds = campaigns.operationSequenceDirtyCampaignIds.filter(
        (dirtyCampaignId) => dirtyCampaignId !== campaignId,
      );
      campaigns.timeTracker = timeTracker;
      const campaignIndex = campaigns.list.findIndex((campaign) => campaign.campaignId === campaignId);

      if (campaignIndex !== -1) {
        campaigns.list[campaignIndex].start = start;
        campaigns.list[campaignIndex].operationSequence = operationSequence;
      }
    },
    campaignTimeTrackerGenerateFailed: (campaigns) => {
      campaigns.isGeneratingTimeTracker = false;
    },
    campaignTimeTrackerGenerationConflict: (campaigns, action) => {
      campaigns.isGeneratingTimeTracker = false;
      campaigns.timeTrackerGenerationConflict = action.payload;
    },
    campaignTimeTrackerGenerationConflictClosed: (campaigns) => {
      campaigns.timeTrackerGenerationConflict = null;
    },
    timeTrackerStartUpdated: (campaigns, action) => {
      const { campaignId, start, timeTracker } = action.payload;
      campaigns.timeTracker = timeTracker;
      campaigns.timeTrackerCampaignId = campaignId;
      const campaignIndex = campaigns.list.findIndex((campaign) => campaign.campaignId === campaignId);

      if (campaignIndex !== -1) {
        campaigns.list[campaignIndex].start = start;
      }
    },
    timeTrackerItemUpdated: (campaigns, action) => {
      if (Array.isArray(action.payload)) {
        campaigns.timeTracker = action.payload;
        return;
      }
      campaigns.timeTracker = action.payload.timeTracker;
      campaigns.timeTrackerCampaignId = action.payload.campaignId;
    },
  },
});

export const {
  listRequested,
  listFetched,
  listRequestFailed,
  campaignAddRequested,
  campaignAdded,
  campaignSelected,
  campaignDetailsRequested,
  campaignDetailsFetched,
  campaignDetailsRequestFailed,
  campaignRemoved,
  campaignModalOpened,
  campaignModalClosed,
  estimateRemoved,
  learningCurveModalUpdated,
  learningCurveUpdateRequested,
  learningCurveUpdated,
  learningCurveUpdateRequestFailed,
  learningCurveFlagUpdateRequested,
  reorderEstimatesRequested,
  kpiModalUpdated,
  kpisAddRequested,
  kpisAdded,
  kpisAddRequestFailed,
  kpiRemoved,
  welldesignModalUpdated,
  campaignConceptDesignsAdded,
  conceptDesignRemoved,
  campaignRunConceptFetched,
  simulationRequested,
  simulationFetched,
  simulationRequestFailed,
  addSimulationStatus,
  campaignSimulationFetched,
  campaignSimulationRequested,
  campaignSimulationRequestFailed,
  designsFetched,
  campaignAddRequestedFailed,
  designsFetchFailed,
  campaignResultsFetched,
  campaignResultsRequested,
  runCampaignTotalRequested,
  runCampaignTotalFetched,
  campaignsCleanup,
  updateIsRecalculating,
  campaignRunConceptRequested,
  campaignRunConcepRequestedFailed,
  reorderEstimatesFetched,
  timeTrackerRequested,
  timeTrackerFetched,
  timeTrackerRequestFailed,
  operationSequenceReorderRequested,
  operationSequenceReorderFetched,
  campaignTimeTrackerGenerateRequested,
  campaignTimeTrackerGenerated,
  campaignTimeTrackerGenerateFailed,
  campaignTimeTrackerGenerationConflict,
  campaignTimeTrackerGenerationConflictClosed,
  timeTrackerStartUpdated,
  timeTrackerItemUpdated,
} = campaigns.actions;
export default campaigns.reducer;

/**
 * Get campaigns data
 *
 * @param companyId
 */
export const getCampaigns = (companyId: string) =>
  apiCallBegan({
    url: `/api/campaigns/${companyId}`,
    method: 'GET',
    params: { summary: true },
    onStart: listRequested.type,
    onSuccess: (response) => ({
      type: listFetched.type,
      payload: response,
    }),
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: i18n.t(translations.campaigns_failedToGetCampaigns) as string,
        },
      });
      return {
        type: listRequestFailed.type,
      };
    },
  });

export const getCampaignDetails = (campaignId: string) =>
  apiCallBegan({
    url: `/api/campaign/${campaignId}/details`,
    method: 'GET',
    onStart: () => ({
      type: campaignDetailsRequested.type,
      payload: { campaignId },
    }),
    onSuccess: (response) => ({
      type: campaignDetailsFetched.type,
      payload: response,
    }),
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: i18n.t(translations.campaigns_failedToGetCampaigns) as string,
        },
      });
      return {
        type: campaignDetailsRequestFailed.type,
        payload: { campaignId },
      };
    },
  });

export const addCampaign = (companyId: string, data: { campaignId?: string; name: string; status?: string }) =>
  apiCallBegan({
    url: `/api/campaigns/${companyId}`,
    method: 'POST',
    data: { ...data, start: null },
    onStart: campaignAddRequested.type,
    onSuccess: (response) => ({
      type: campaignAdded.type,
      payload: response,
    }),
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: i18n.t(translations.campaigns_failedToAdd) as string,
        },
      });
      return {
        type: campaignAddRequestedFailed.type,
      };
    },
  });

export const addInitialCampaign = (companyId: string, name: string, status: string = CampaignStatus.Prototype) =>
  addCampaign(companyId, { name, status });

interface ICampaignStatusUpdate {
  campaignId: string;
  status: string;
}

export const updateCampaignStatus = (companyId: string, data: ICampaignStatusUpdate) =>
  apiCallBegan({
    url: `/api/campaigns/${data.campaignId}/status`,
    method: 'PATCH',
    params: { companyId },
    data: { status: data.status ?? CampaignStatus.Prototype },
    onSuccess: (response) => ({
      type: campaignAdded.type,
      payload: response,
    }),
    onError: (error: ICampaignStatusError) => {
      toast({
        message: {
          type: 'Error',
          content: getCampaignStatusErrorContent(error),
        },
      });
    },
  });

export const requestCampaignStatusChange = (
  companyId: string,
  data: ICampaignStatusUpdate,
  onConfirmationRequired: () => void,
) => {
  if (data.status !== CampaignStatus.Prototype) {
    return updateCampaignStatus(companyId, data);
  }

  return apiCallBegan({
    url: `/api/campaigns/${data.campaignId}/time-tracker`,
    method: 'GET',
    onSuccess: (timeTracker: campaignTimeTrackerItem[]) => {
      const hasTimeTrackerData = timeTracker.some((estimate) => estimate.operations?.length > 0);
      if (hasTimeTrackerData) {
        onConfirmationRequired();
        return;
      }
      return updateCampaignStatus(companyId, data);
    },
  });
};

export const removeCampaign = (id: string) =>
  apiCallBegan({
    url: `/api/campaigns/${id}`,
    method: 'DELETE',
    onSuccess: () => ({
      type: campaignRemoved.type,
      payload: id,
    }),
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: i18n.t(translations.campaigns_failedToDelete) as string,
        },
      });
    },
  });

export const removeEstimate = (id: string, campaignId: string) =>
  apiCallBegan({
    url: `/api/campaign/estimate/${id}`,
    method: 'DELETE',
    onSuccess: () => ({
      type: estimateRemoved.type,
      payload: { id, campaignId },
    }),
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: i18n.t(translations.campaigns_failedToDelete) as string,
        },
      });
    },
  });

export const updateLearningCurve = (campaignId: string, data: ILearningCurveData[]) => {
  const estimates = data.map((estimate) => ({
    campaignsEstimatesId: estimate.campaignsEstimatesId,
    adjustment: estimate.adjustment,
  }));

  return apiCallBegan({
    url: `/api/campaigns/learning-curve`,
    method: 'POST',
    data: { estimates },
    onStart: () => ({
      type: learningCurveUpdateRequested.type,
      payload: { campaignId, estimates },
    }),
    onSuccess: learningCurveUpdated.type,
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: i18n.t(translations.campaigns_failedToUpdateLearningCurve) as string,
        },
      });
      return {
        type: learningCurveUpdateRequestFailed.type,
      };
    },
  });
};

export const updateLearningCurveFlag = (campaignId: string) =>
  apiCallBegan({
    url: `/api/campaigns/learning-curve/${campaignId}`,
    method: 'POST',
    data: {},
    onStart: () => ({
      type: learningCurveFlagUpdateRequested.type,
      payload: { campaignId },
    }),
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: i18n.t(translations.campaigns_failedToUpdateLearningCurveFlag) as string,
        },
      });
    },
  });

export const addKpis = (campaignId: string, data: { kpis: string[] }) => {
  const kpis = data.kpis.map((kpi: string) => kpi);

  return apiCallBegan({
    url: `/api/campaign/${campaignId}/add-kpis`,
    method: 'POST',
    data: { kpis },
    onStart: kpisAddRequested.type,
    onSuccess: (response) => ({
      type: kpisAdded.type,
      payload: response,
    }),
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: i18n.t(translations.campaigns_failedToAdd) as string,
        },
      });
      return {
        type: kpisAddRequestFailed.type,
      };
    },
  });
};

export const deleteKpi = (id: string, campaignId: string) =>
  apiCallBegan({
    url: `/api/campaign/kpi/${id}`,
    method: 'DELETE',
    onSuccess: () => ({
      type: kpiRemoved.type,
      payload: { id, campaignId },
    }),
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: i18n.t(translations.campaigns_failedToDeleteKpi) as string,
        },
      });
    },
  });

export const addConceptDesigns = (data: any) =>
  apiCallBegan({
    url: 'api/campaign/concept-designs',
    method: 'POST',
    data,
    onStart: campaignAddRequested.type,
    onSuccess: campaignConceptDesignsAdded.type,
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: i18n.t(translations.campaigns_failedToAddConceptDesigns) as string,
        },
      });
      return {
        type: campaignAddRequestedFailed.type,
      };
    },
  });

export const removeConceptDesign = (campaignId: string, datasetId: number | null, projectId: string | null) =>
  apiCallBegan({
    url: `/api/campaign/remove-concept-design`,
    method: 'DELETE',
    data: { campaignId, datasetId, projectId },
    onSuccess: conceptDesignRemoved.type,
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: i18n.t(translations.campaigns_failedToDeleteConceptDesign) as string,
        },
      });
    },
  });

export const runConceptCampaign = (data: IRunCampaignData) =>
  apiCallBegan({
    url: 'api/campaign/concept',
    method: 'POST',
    data,
    onStart: campaignRunConceptRequested.type,
    onSuccess: (response) => ({
      type: campaignRunConceptFetched.type,
      payload: response,
    }),
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: i18n.t(translations.campaigns_failedToRunConceptCampaign) as string,
        },
      });
      return {
        type: campaignRunConcepRequestedFailed.type,
      };
    },
  });

export const getSimulation = (projectId: string) =>
  apiCallBegan({
    url: `/api/simulations/${projectId}`,
    method: 'GET',
    onStart: () => ({ type: simulationRequested.type, payload: { projectId } }),
    onSuccess: (response) => ({
      type: simulationFetched.type,
      payload: { projectId, ...response },
    }),
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: i18n.t(translations.campaigns_errorGettingSimulationData) as string,
        },
      });
      return {
        type: simulationRequestFailed.type,
        payload: { projectId },
      };
    },
  });

export const getCampaignSimulation = (campaignId: string) =>
  apiCallBegan({
    url: `/api/simulations/campaign/${campaignId}`,
    method: 'GET',
    onStart: () => ({
      type: campaignSimulationRequested.type,
      payload: { campaignId },
    }),
    onSuccess: (response) => ({
      type: campaignSimulationFetched.type,
      payload: { campaignId, ...response },
    }),
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: i18n.t(translations.campaigns_errorGettingCampaignSimulationData) as string,
        },
      });
      return {
        type: campaignSimulationRequestFailed.type,
        payload: { campaignId },
      };
    },
  });

export const getDesigns = (companyId: string, campaignId?: string) =>
  apiCallBegan({
    url: `/api/campaigns/designs/${companyId}${campaignId ? `?campaignId=${campaignId}` : ''}`,
    method: 'GET',
    onStart: listRequested.type,
    onSuccess: designsFetched.type,
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: i18n.t(translations.campaigns_failedToGetDesigns) as string,
        },
      });
      return {
        type: designsFetchFailed.type,
      };
    },
  });

export const getCampaignResults = (campaignId: string) =>
  apiCallBegan({
    url: `api/campaigns/campaign-results/${campaignId}`,
    method: 'GET',
    onStart: campaignResultsRequested.type,
    onSuccess: campaignResultsFetched.type,
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: i18n.t(translations.campaigns_failedToGetCampaignResults) as string,
        },
      });
      return {
        type: campaignAddRequestedFailed.type,
      };
    },
  });

export const runCampaignTotalSimulation = (campaignId: string) =>
  apiCallBegan({
    url: `api/campaigns/${campaignId}/run-campaign-total`,
    method: 'GET',
    onStart: () => ({
      type: runCampaignTotalRequested.type,
      payload: { campaignId },
    }),
    onSuccess: (response) => ({
      type: runCampaignTotalFetched.type,
      payload: response,
    }),
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: i18n.t(translations.campaigns_failedToRunCampaignTotalSimulation) as string,
        },
      });
      return {
        type: campaignAddRequestedFailed.type,
      };
    },
  });

export const reorderCampaignEstimates = (data: { campaignId: string; from: number; to: number }) =>
  apiCallBegan({
    url: 'api/campaign/estimate/reorder',
    method: 'POST',
    data,
    onStart: () => ({
      type: reorderEstimatesRequested.type,
      payload: data,
    }),
    onSuccess: () => ({
      type: reorderEstimatesFetched.type,
    }),
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: i18n.t(translations.campaigns_failedToReorderCampaignEstimates) as string,
        },
      });
      return {
        type: reorderEstimatesFetched.type,
      };
    },
  });

export const reorderCampaignOperationSequence = (data: {
  campaignId: string;
  sequence: ICampaignOperationSequenceItem[];
}) =>
  apiCallBegan({
    url: `/api/campaigns/${data.campaignId}/operation-sequence`,
    method: 'POST',
    data: { sequence: data.sequence },
    onStart: () => ({
      type: operationSequenceReorderRequested.type,
      payload: data,
    }),
    onSuccess: operationSequenceReorderFetched.type,
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: i18n.t(translations.campaigns_failedToReorderCampaignOperationSequence) as string,
        },
      });
      return {
        type: operationSequenceReorderFetched.type,
        payload: {
          campaignId: data.campaignId,
          operationSequence: data.sequence,
        },
      };
    },
  });

export const getCampaignTimeTracker = (campaignId: string) =>
  apiCallBegan({
    url: `/api/campaigns/${campaignId}/time-tracker`,
    method: 'GET',
    onStart: () => ({
      type: timeTrackerRequested.type,
      payload: campaignId,
    }),
    onSuccess: (response) => ({
      type: timeTrackerFetched.type,
      payload: {
        campaignId,
        timeTracker: response,
      },
    }),
    onError: timeTrackerRequestFailed.type,
  });

export const generateCampaignTimeTracker = (data: {
  campaignId: string;
  sequence: ICampaignOperationSequenceItem[];
  force?: boolean;
}) =>
  apiCallBegan({
    url: `/api/campaigns/${data.campaignId}/time-tracker/generate`,
    method: 'POST',
    data: { sequence: data.sequence, force: data.force ?? false },
    onStart: campaignTimeTrackerGenerateRequested.type,
    onSuccess: campaignTimeTrackerGenerated.type,
    onError: (error) => {
      if (error.status === 409 && error.code === 'CAMPAIGN_TIME_TRACKER_EXISTS') {
        return {
          type: campaignTimeTrackerGenerationConflict.type,
          payload: {
            campaignId: data.campaignId,
            sequence: data.sequence,
          },
        };
      }

      toast({
        message: {
          type: 'Error',
          content: i18n.t(translations.campaigns_failedToGenerateCampaignTimeTracker) as string,
        },
      });
      return {
        type: campaignTimeTrackerGenerateFailed.type,
      };
    },
  });

export const updateTimeTrackerStart = (campaignId: string, start: string) => {
  return apiCallBegan({
    url: `/api/campaigns/${campaignId}/start`,
    method: 'POST',
    data: { start },
    onSuccess: timeTrackerStartUpdated.type,
  });
};
export const updateTimeTrackerTask = (data: any) => {
  const { campaignId, taskId, ...payload } = data;
  const { field, value } = payload;
  return apiCallBegan({
    url: `/api/campaigns/${campaignId}/time-tracker/${taskId}`,
    method: 'PUT',
    data: { field, value },
    onSuccess: (response) => ({
      type: timeTrackerItemUpdated.type,
      payload: {
        campaignId,
        timeTracker: response,
      },
    }),
  });
};
